import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCopilot, AskAgentButton } from '../copilot';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  BookOpen,
  Plus,
  MapPin,
  Calendar,
  Tag,
  Lock,
  Flag,
  User,
  Sun,
  Moon,
  Upload,
  Download,
} from '@phosphor-icons/react';

export const StationLogBookPage = () => {
  const { api, user, isAdmin, isSupervisorPlus, isCrewLeaderPlus, isAuditor } = useAuth();
  const { openCopilot, setEntityContext } = useCopilot();
  const fileInputRef = useRef(null);
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLogOpen, setCreateLogOpen] = useState(false);
  
  // Station upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // New log form
  const [newLog, setNewLog] = useState({
    entry_text: '',
    shift: 'day',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    try {
      const response = await api.get('/stations');
      setStations(response.data || []);
      if (response.data?.length > 0) {
        handleSelectStation(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch stations');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (stationId) => {
    try {
      const response = await api.get(`/stations/${stationId}/logs`);
      setLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      setLogs([]);
    }
  };

  const handleSelectStation = async (station) => {
    setSelectedStation(station);
    setEntityContext({ station_id: station.id });
    await fetchLogs(station.id);
  };

  // Ask Agent prompts for station context
  const stationAgentPrompts = selectedStation ? [
    { label: 'Summarize Shift', prompt: 'Summarize the recent shift activities and key events at this station.', context: { station_id: selectedStation.id } },
    { label: 'Find Patterns', prompt: 'Identify patterns and trends in the station log entries.', context: { station_id: selectedStation.id } },
    { label: 'Draft Handoff', prompt: 'Draft a shift handoff report for the incoming crew.', context: { station_id: selectedStation.id } },
  ] : [];

  const handleCreateLog = async (e) => {
    e.preventDefault();
    if (!selectedStation) return;

    try {
      await api.post(`/stations/${selectedStation.id}/logs`, newLog);
      toast.success('Log entry created successfully');
      setCreateLogOpen(false);
      setNewLog({ entry_text: '', shift: 'day', tags: [] });
      await fetchLogs(selectedStation.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create log entry');
    }
  };

  const handleFlagLog = async (logId) => {
    const note = prompt('Enter flag note:');
    if (!note) return;

    try {
      await api.put(`/station-logs/${logId}/flag`, { note });
      toast.success('Log entry flagged');
      await fetchLogs(selectedStation.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to flag log entry');
    }
  };

  const handleLockLog = async (logId) => {
    const reason = prompt('Enter lock reason:');
    if (!reason) return;

    try {
      await api.post(`/station-logs/${logId}/lock`, { reason });
      toast.success('Log entry locked');
      await fetchLogs(selectedStation.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock log entry');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !newLog.tags.includes(tagInput.trim())) {
      setNewLog({ ...newLog, tags: [...newLog.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewLog({ ...newLog, tags: newLog.tags.filter(t => t !== tag) });
  };

  const canCreateLog = () => {
    return isCrewLeaderPlus() && !isAuditor();
  };

  // Station Upload Functions
  const downloadStationTemplate = () => {
    const csv = 'substation,address\nMain Street Substation,123 Main St Springfield\nDowntown Hub,456 Central Ave Springfield';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'station_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const handleStationFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      if (!headers.includes('substation') && !headers.includes('name')) {
        toast.error('CSV must have "substation" or "name" column');
        return;
      }
      
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] || '';
        });
        rows.push(row);
      }
      
      setUploadPreview({ file, headers, rows: rows.slice(0, 10), totalRows: rows.length });
    };
    reader.readAsText(file);
  };

  const handleStationUpload = async () => {
    if (!uploadPreview?.file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadPreview.file);
    
    try {
      const response = await api.post('/admin/stations/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Uploaded! ${response.data.rows_created} created, ${response.data.rows_updated} updated`);
      if (response.data.rows_failed > 0) {
        toast.warning(`${response.data.rows_failed} rows failed`);
      }
      
      setUploadDialogOpen(false);
      setUploadPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Refresh stations
      fetchStations();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="station-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
            Station Log Book
          </h1>
          <p className="text-slate-600">
            Record and review station activities and events
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={() => setUploadDialogOpen(true)}
              data-testid="upload-stations-btn"
            >
              <Upload size={16} className="mr-2" />
              Upload Stations
            </Button>
          )}
          {canCreateLog() && selectedStation && (
            <Dialog open={createLogOpen} onOpenChange={setCreateLogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="create-log-btn">
                  <Plus size={20} className="mr-2" />
                  New Entry
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Log Entry</DialogTitle>
                <DialogDescription>
                  Add a new entry to {selectedStation.name}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateLog} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="entry_text">Entry</Label>
                  <Textarea
                    id="entry_text"
                    value={newLog.entry_text}
                    onChange={(e) => setNewLog({ ...newLog, entry_text: e.target.value })}
                    placeholder="Describe the activity or event..."
                    rows={4}
                    required
                    data-testid="log-entry-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Shift</Label>
                  <Select
                    value={newLog.shift}
                    onValueChange={(value) => setNewLog({ ...newLog, shift: value })}
                  >
                    <SelectTrigger data-testid="shift-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">
                        <div className="flex items-center gap-2">
                          <Sun size={16} /> Day Shift
                        </div>
                      </SelectItem>
                      <SelectItem value="night">
                        <div className="flex items-center gap-2">
                          <Moon size={16} /> Night Shift
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder="Add a tag..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      data-testid="tag-input"
                    />
                    <Button type="button" variant="outline" onClick={addTag}>
                      Add
                    </Button>
                  </div>
                  {newLog.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newLog.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                          {tag} ×
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-primary" data-testid="submit-log-btn">
                    Create Entry
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Stations List */}
        <div className="lg:col-span-1">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2">
                <MapPin size={20} />
                Stations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {loading ? (
                  <div className="p-4 text-center text-slate-500">Loading...</div>
                ) : stations.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">No stations</div>
                ) : (
                  stations.map((station) => (
                    <button
                      key={station.id}
                      onClick={() => handleSelectStation(station)}
                      className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${
                        selectedStation?.id === station.id ? 'bg-primary/5 border-l-4 border-primary' : ''
                      }`}
                      data-testid={`station-${station.id}`}
                    >
                      <h3 className="font-medium text-slate-900">{station.name}</h3>
                      {station.code && (
                        <p className="text-sm text-slate-500 font-mono">{station.code}</p>
                      )}
                    </button>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Log Entries */}
        <div className="lg:col-span-3">
          {selectedStation ? (
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-display flex items-center gap-2">
                      <BookOpen size={20} />
                      {selectedStation.name} Log
                    </CardTitle>
                    <CardDescription>
                      {logs.length} entries
                    </CardDescription>
                  </div>
                  <AskAgentButton
                    prompts={stationAgentPrompts}
                    onPromptSelect={(prompt, ctx) => openCopilot(prompt, ctx)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <BookOpen size={48} className="mx-auto mb-4 text-slate-300" />
                    <p>No log entries yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {logs.map((log) => (
                      <div
                        key={log.id}
                        className={`p-4 rounded-lg border ${
                          log.locked ? 'bg-slate-50 border-slate-300' : 'bg-white border-slate-200'
                        }`}
                        data-testid={`log-entry-${log.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-slate-400" />
                              <span className="font-medium text-sm">{log.author_name}</span>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {log.shift === 'day' ? (
                                <><Sun size={12} className="mr-1" /> Day</>
                              ) : (
                                <><Moon size={12} className="mr-1" /> Night</>
                              )}
                            </Badge>
                            {log.locked && (
                              <Badge className="bg-slate-200 text-slate-700">
                                <Lock size={12} className="mr-1" /> Locked
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-slate-500">
                            {new Date(log.created_at).toLocaleString()}
                          </span>
                        </div>

                        <p className="text-slate-700 mb-3 whitespace-pre-wrap">{log.entry_text}</p>

                        {log.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {log.tags.map((tag, i) => (
                              <Badge
                                key={i}
                                variant={tag.startsWith('FLAG:') ? 'destructive' : 'secondary'}
                                className="text-xs"
                              >
                                <Tag size={12} className="mr-1" />
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {!log.locked && !isAuditor() && (
                          <div className="flex gap-2 pt-2 border-t border-slate-100">
                            {isSupervisorPlus() && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleFlagLog(log.id)}
                                data-testid={`flag-log-${log.id}`}
                              >
                                <Flag size={14} className="mr-1" />
                                Flag
                              </Button>
                            )}
                            {isAdmin() && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleLockLog(log.id)}
                                data-testid={`lock-log-${log.id}`}
                              >
                                <Lock size={14} className="mr-1" />
                                Lock
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <BookOpen size={48} className="text-slate-300 mb-4" />
                <p className="text-slate-500">Select a station to view logs</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Station Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Station List</DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk create or update stations
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template Download */}
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-sm">CSV Template</p>
                <p className="text-xs text-slate-500">Columns: substation, address</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadStationTemplate}>
                <Download size={14} className="mr-1" />
                Download
              </Button>
            </div>

            {/* File Input */}
            <div>
              <Label htmlFor="station-csv">Select CSV File</Label>
              <input
                ref={fileInputRef}
                type="file"
                id="station-csv"
                accept=".csv"
                onChange={handleStationFileSelect}
                className="mt-1 block w-full text-sm text-slate-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-medium
                  file:bg-primary file:text-white
                  hover:file:bg-primary/90
                  cursor-pointer"
                data-testid="station-csv-input"
              />
            </div>

            {/* Preview */}
            {uploadPreview && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-3 py-2 text-sm font-medium flex justify-between items-center">
                  <span>Preview ({uploadPreview.totalRows} rows)</span>
                  <Badge variant="secondary">{uploadPreview.file.name}</Badge>
                </div>
                <div className="max-h-48 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {uploadPreview.headers.map(h => (
                          <TableHead key={h} className="text-xs">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uploadPreview.rows.map((row, idx) => (
                        <TableRow key={idx}>
                          {uploadPreview.headers.map(h => (
                            <TableCell key={h} className="text-xs py-1">{row[h]}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setUploadDialogOpen(false);
              setUploadPreview(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleStationUpload} 
              disabled={!uploadPreview || uploading}
              data-testid="confirm-station-upload-btn"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Upload Stations
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StationLogBookPage;
